/* eslint-disable react-hooks/exhaustive-deps */
import { Table, Button, DatePicker, Switch, Pagination, Form, Input, Select, Modal, theme } from "antd";
import axios from "axios";
import dayjs from "dayjs";
import parse from "html-react-parser";
import { useState, useEffect } from "react";

import { searchOptions, orderByColumnNames } from "./helper.jsx";
import {
  showErrorNotification,
  showSuccessNotification,
  showWarningNotification
} from "../../../utils/notification.jsx";
import { clamp } from "../../../utils/responsiveHelpers.jsx";

const CommentModeration = ({ ROOT_URL }) => {
  const BASE_BACKEND_URL = "admin/cp-sheet/comment-moderation";
  const currentDate = new Date();
  const oneMonthAgo = new Date(
    currentDate.getFullYear(),
    currentDate.getMonth() - 1,
    currentDate.getDate()
  );
  const [form] = Form.useForm();
  const { confirm } = Modal;
  const { token } = theme.useToken();

  const [commentsList, setCommentsList] = useState([]);
  const [showDeleted, setShowDeleted] = useState(false);
  const [filter, setFilter] = useState({
    showDeleted,
    startDate: oneMonthAgo,
    endDate: currentDate,
    orderByColumnName: "createdAtDesc"
  });
  const [totalComments, setTotalComments] = useState(50);
  const [pageSize] = useState(50);
  const [currentPage, setCurrentPage] = useState(1);

  const prefixSelector = (
    <Form.Item
      name="label"
      rules={[{ required: true, message: "Search label is required" }]}
      noStyle
    >
      <Select placeholder="Search Label" options={searchOptions} size="large" />
    </Form.Item>
  );

  const fetchCommentsList = (query = "", page) => {
    axios
      .get(`${ROOT_URL}/${BASE_BACKEND_URL}/list`, {
        params: {
          query,
          filter,
          currentPage: page || 1
        }
      })
      .then((res) => {
        const { comments, commentsCount } = res.data;
        setCommentsList(comments);
        setTotalComments(commentsCount);
        showSuccessNotification(
          filter.showDeleted
            ? "Deleted Comments fetched successfully!"
            : "Comments fetched successfully!"
        );
      })
      .catch((err) => {
        showErrorNotification(err);
      });
  };

  const handleCommentSearch = (query, page) => {
    fetchCommentsList(query, page);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    const query = form.getFieldValue();
    if (query["search"] !== null && query["label"] !== undefined) {
      handleCommentSearch(query, page);
      return;
    }
    fetchCommentsList("", page);
  };

  const handleDeleteDiscussionMessage = (messageId) => {
    axios
      .post(`${ROOT_URL}/admin/cp-sheet/problems/discussion/delete-message`, {
        messageId
      })
      .then(() => {
        fetchCommentsList();
        showSuccessNotification("Discussion Message deleted successfully!");
      })
      .catch((err) => {
        showErrorNotification(err);
      });
  };

  const showConfirm = (commentId, isParentMessage) => {
    const confirmMessage = isParentMessage
      ? "This action will delete all the child messages as well."
      : "This action will delete a message.";
    confirm({
      content: <span>{`Are you sure you want to delete this message? ${confirmMessage}`}</span>,
      onOk() {
        showWarningNotification(confirmMessage);
        handleDeleteDiscussionMessage(commentId);
      },
      onCancel() {}
    });
  };

  const columns = [
    {
      title: "S No.",
      dataIndex: "index",
      key: "index",
      width: 80
    },
    {
      title: "User Name",
      dataIndex: "userName",
      key: "userName",
      width: 170
    },
    {
      title: "Problem Name",
      dataIndex: "problemName",
      key: "problemName",
      truncate: true,
      width: 170
    },
    {
      title: "Content",
      dataIndex: "content",
      key: "content",
      truncate: true,
      render: (content) => (
        <div
          style={{
            maxWidth: "40dvw",
            whiteSpace: "break-word"
          }}
        >
          {parse(content)}
        </div>
      )
    },
    {
      title: "Replies Count",
      dataIndex: "repliesCount",
      key: "repliesCount"
    },
    {
      title: "Likes Count",
      dataIndex: "likesCount",
      key: "likesCount"
    },
    {
      title: "Is Parent Message",
      dataIndex: "isParentMessage",
      key: "isParentMessage",
      render: (isParentMessage) => (isParentMessage ? "Yes" : "No")
    },
    {
      title: "Created At",
      dataIndex: "createdAt",
      key: "createdAt",
      width: 150,
      render: (createdAt) => (createdAt ? dayjs(createdAt).format("DD MMM YYYY | hh:mm") : "--")
    },
    {
      title: "Actions",
      key: "action",
      width: 100,
      render: (row) => (
        <div
          id="cp_sheet_problem_discussion_messages"
          style={{
            display: "flex",
            justifyContent: "space-around",
            gap: "5px"
          }}
        >
          <Button
            type="primary"
            danger
            disabled={showDeleted}
            onClick={() => {
              showConfirm(row.key, row.isParentMessage);
            }}
          >
            Delete Message
          </Button>
        </div>
      ),
      hidden: showDeleted
    },
    {
      title: "Deleted By",
      dataIndex: "deletedBy",
      key: "deletedBy",
      width: 150,
      render: (deletedBy) => deletedBy || "--",
      hidden: !showDeleted
    }
  ];

  const visibleColumns = columns.filter((column) => !column.hidden);

  useEffect(() => {
    fetchCommentsList();
  }, [filter]);

  return (
    <div
      style={{
        marginTop: "20px",
        marginBottom: "20px",
        marginLeft: clamp(12, 30),
        marginRight: clamp(12, 30),
        display: "flex",
        flexDirection: "column",
        gap: "10px",
        alignItems: "center"
      }}
    >
      <Form
        form={form}
        name="search"
        onFinish={() => handleCommentSearch(form.getFieldValue(), currentPage)}
        style={{ width: "100%" }}
      >
        <Form.Item name="search" rules={[{ required: true, message: "Search value is required!" }]}>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center" }}>
            <Input
              addonBefore={prefixSelector}
              style={{ flex: "1 1 auto", width: "min(240px, 100%)" }}
              size="large"
              placeholder="Search Users by Problem Name, Content"
              allowClear
            />
            <Button
              type="primary"
              style={{ width: "min(150px, 100%)" }}
              htmlType="submit"
              size="large"
            >
              Search
            </Button>
          </div>
        </Form.Item>
      </Form>
      <div
        style={{
          display: "flex",
          gap: "10px",
          alignItems: "center",
          flexWrap: "wrap",
          width: "100%"
        }}
      >
        <div style={{ display: "flex", gap: "10px", alignItems: "center", width: "100%" }}>
          {" "}
          Select Date Range:{" "}
          <DatePicker.RangePicker
            style={{ width: "min(320px, 100%)" }}
            value={
              filter?.startDate && filter?.endDate
                ? [dayjs(filter.startDate), dayjs(filter.endDate)]
                : []
            }
            onChange={(dates) => {
              if (dates) {
                setFilter((prevFilter) => ({
                  ...prevFilter,
                  startDate: dates[0].clone().startOf("day").toDate(),
                  endDate: dates[1].clone().endOf("day").toDate()
                }));
              } else {
                setFilter((prevFilter) => ({
                  ...prevFilter,
                  startDate: null,
                  endDate: null
                }));
              }
            }}
          />
        </div>
        <div style={{ display: "flex", gap: "10px", alignItems: "center", width: "100%" }}>
          Show Deleted Comments{" "}
          <Switch
            checked={showDeleted}
            onChange={(checked) => {
              setShowDeleted(checked);
              setFilter((prevFilter) => ({
                ...prevFilter,
                showDeleted: checked
              }));
            }}
          />
        </div>{" "}
        <div
          style={{
            display: "flex",
            gap: "10px",
            alignItems: "center",
            width: "100%"
          }}
        >
          Order By:{" "}
          <Select
            placeholder="Select Column"
            style={{ width: "min(200px, 100%)" }}
            value={filter.orderByColumnName}
            onChange={(value) => {
              setFilter((prevFilter) => ({
                ...prevFilter,
                orderByColumnName: value
              }));
            }}
          >
            {(orderByColumnNames || []).map((item) => (
              <Select.Option value={item.value} key={item.value}>
                {item.label}
              </Select.Option>
            ))}
          </Select>
        </div>
      </div>
      <Table
        columns={visibleColumns}
        dataSource={commentsList}
        pagination={false}
        style={{ width: "100%" }}
        scroll={{ x: "max-content" }}
        rowClassName={(record) => (record.isParentMessage ? "parent-message-row" : "")}
        onRow={(record) => ({
          style: record.isParentMessage ? { backgroundColor: token.colorFillTertiary } : {}
        })}
      />
      <br />
      <Pagination
        total={totalComments}
        showSizeChanger={false}
        showQuickJumper={false}
        pageSize={pageSize}
        current={currentPage}
        onChange={handlePageChange}
      />
    </div>
  );
};

export default CommentModeration;
